import { FormControl, FormControlLabel, InputLabel, TextField } from '@mui/material';

import MyDialog from "~/mui/MyDialog";
import { checkForErrorsState } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { apiCreateService } from '~/providers/api';
import { ICreateService, IService, TServiceType } from "~/providers/interfaces";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import TextFieldNumber from "~/mui/TextFieldNumber";
import { MySwitch } from "~/mui/MySwitch";
import { MyMenuItem, MySelect } from "~/mui/MySelect";

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
}

// data formulare
interface IStateService extends ICreateService {}

interface IRootServiceItem {
	id: number | null;
	title: string;
}

// chyby formulare
type TStateServiceErrors = {[key in keyof Pick<ICreateService, "service" | "callPrice" | "rootServiceId">]: string; };

interface ICreateServiceModal {
	rootServices: Array<IService>;
	onSave?: (project: IService) => void;
	onClose?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function CreateServiceModal({
	rootServices,
	onSave = () => {},
	onClose = () => {},
	onChange = () => {},
}: ICreateServiceModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
	});
	const { state: stateService, updateState: updateStateService } = myUseState<IStateService>({
		service: "",
		callPrice: 0,
		hiddenForUsers: false,
		regularService: true,
		rootServiceId: 0,
		serviceType: "ENDPOINT",
	});
	const { state: stateServiceError, setState: setStateServiceError } = myUseState<TStateServiceErrors>({
		service: "",
		callPrice: "",
		rootServiceId: "",
	});
	const allRootServices: Array<IRootServiceItem> = [
		{
			id: 0,
			title: trans("createServiceModal.type.emptyRoot"),
		},
		...rootServices.map(item => ({
			id: item.id,
			title: item.service,
		})),
	];

	function getWasChanged(newState: Partial<IStateService>): boolean {
		const newStateObj = {
			...stateService,
			...newState,
		};

		return newStateObj.service !== ""
			|| newStateObj.callPrice !== 0;
	}

	function updateStateServiceWithChange(newState: Partial<IStateService>) {
		const wasChanged = getWasChanged(newState);

		updateStateService(newState);
		updateState({
			wasChanged,
		});
		onChange(wasChanged);
	}

	async function dialogOnSave() {
		// validTill - nemuze byt prazdne
		const errorData = checkForErrorsState<IStateService, TStateServiceErrors>(stateService, {
			service: { check: "string-gt0", msg: trans("createServiceModal.error.service") },
			callPrice: { check: "number-eg0", msg: trans("createServiceModal.error.callPrice") },
		});

		setStateServiceError(errorData.errorsState);

		// jsou tam chyby
		if (errorData.errorsCount > 0) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const sendData: ICreateService = {
			...stateService,
			rootServiceId: stateService.rootServiceId || null,

		};
		const outputData = await apiCreateService(sendData, lang);

		if (outputData.noErrors) {
			onSave(outputData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: outputData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("createServiceModal.title")} onClose={onClose} onSave={dialogOnSave} className="createServiceModal" dialogLoading={state.dialogLoading} disableSave={!state.wasChanged}
		errorMsg={state.errorMsg} saveCaption={trans("adminServicesPage.addService")} confirmClose={state.wasChanged} minWidth="300px">
		<FormControl sx={{ width: "100%" }}>
			<TextField
				label={trans("createServiceModal.service")}
				value={stateService.service}
				onChange={event => updateStateServiceWithChange({ service: event.target.value })}
				color="success"
				autoComplete="off"
				sx={{ width: "100%", marginBottom: "var(--margin-large)" }}
				error={stateServiceError.service !== ""}
				helperText={stateServiceError.service}
				size="small"
			/>
			<TextFieldNumber
				label={trans("createServiceModal.callPrice")}
				value={stateService.callPrice}
				onChange={callPrice => updateStateServiceWithChange({ callPrice })}
				sx={{ width: "100%", marginBottom: "var(--margin-large)" }}
				error={stateServiceError.callPrice !== ""}
				helperText={stateServiceError.callPrice}
			/>
			<FormControl size="small" sx={{ width: "100%", marginBottom: "var(--margin-large)" }}>
				<InputLabel>
					{ trans("adminServicesPage.table.serviceType") }
				</InputLabel>
				<MySelect
					value={stateService.serviceType}
					onChange={event => updateStateServiceWithChange({ serviceType: event.target.value as TServiceType })}
					label={trans("adminServicesPage.table.serviceType")}
				>
					<MyMenuItem value={"ENDPOINT" as TServiceType}>
						{ trans("createServiceModal.type.endpoint") }
					</MyMenuItem>
					<MyMenuItem value={"ROOT" as TServiceType}>
						{ trans("createServiceModal.type.root") }
					</MyMenuItem>
				</MySelect>
			</FormControl>
			<FormControl size="small" sx={{ width: "100%", marginBottom: "var(--margin-large)" }}>
				<InputLabel>
					{ trans("adminServicesPage.table.rootServiceId") }
				</InputLabel>
				<MySelect
					value={stateService.rootServiceId}
					onChange={event => updateStateServiceWithChange({ rootServiceId: event.target.value })}
					label={trans("adminServicesPage.table.rootServiceId")}
				>
					{ allRootServices.map(rootItem => <MyMenuItem value={rootItem.id} key={rootItem.id || "root"}>
						{ rootItem.title }
					</MyMenuItem>) }
				</MySelect>
			</FormControl>
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={stateService.hiddenForUsers} onChange={event => updateStateServiceWithChange({ hiddenForUsers: event.target.checked })} />}
				label={trans("adminServicesPage.table.hiddenForUsers")}
				sx={{ marginBottom: "var(--margin-large)" }}
			/>
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={stateService.regularService} onChange={event => updateStateServiceWithChange({ regularService: event.target.checked })} />}
				label={trans("adminServicesPage.table.regularService")}
			/>
		</FormControl>
	</MyDialog>;
}
